import '../ManageLessons.scss';
import { Input, Modal, Select } from '@trackman/web-shared-components';
import { ReactComponent as Plus } from 'images/icons/Plus.svg';
import { ReactComponent as Pencil } from 'images/icons/Pencil.svg';
import { useState } from 'react';
import {
  BaseAdminChapterTest,
  AdminChapterTest,
  AdminEditedLesson,
  ExistingAdminChapterTest,
  AdminLesson,
  AdminLessonType,
  BaseAdminLesson,
} from 'types/admin/modules';
import { VideoLessonField } from '../LessonTypeSpecificFields/VideoLessonField';
import { PictureLessonField } from '../LessonTypeSpecificFields/PictureLessonField';
import { TextLessonField } from '../LessonTypeSpecificFields/TextLessonField';
import { CalculatorLesson, CalculatorLessonField } from '../LessonTypeSpecificFields/CalculatorLessonField';
import { ChapterTestField } from '../LessonTypeSpecificFields/ChapterTestField';
import { getCalcOptionsByType } from 'utilities/adminCalculatorOptions';
import { CalculatorKind } from 'types/calculator/calculatorType';
import { AdminMedia } from 'types/admin/media';

type Props = {
  open: boolean;
  onClose: (payload: AdminLesson | AdminChapterTest | undefined) => void;
  editedLesson: AdminLesson | AdminChapterTest | undefined;
};

type LessonOption = AdminLessonType | 'ChapterTest';

const isLesson = (lesson: BaseAdminLesson | BaseAdminChapterTest | undefined): lesson is BaseAdminLesson => {
  return lesson ? 'kind' in lesson : false;
};

export const EditLessonDialog = ({ open, onClose, editedLesson }: Props) => {
  const defaultCalculatorValue = {
    calculatorKind: 'CALC_NUM' as CalculatorKind,
    calculatorOptions: getCalcOptionsByType('CALC_NUM'),
  };
  const [lessonTitle, setLessonTitle] = useState<string>(editedLesson?.title ?? '');
  const [lessonType, setLessonType] = useState<LessonOption>(
    !editedLesson ? 'Picture' : isLesson(editedLesson) ? editedLesson.kind : 'ChapterTest'
  );
  const [pictureLessonImages, setPictureLessonImages] = useState<AdminMedia[] | undefined>(
    isLesson(editedLesson) ? editedLesson.medias : undefined
  );
  const [pictureLessonDescription, setPictureLessonDescription] = useState<string | undefined>(
    isLesson(editedLesson) ? editedLesson.text : undefined
  );
  const [videoLessonContent, setVideoLessonContent] = useState<string | undefined>(
    isLesson(editedLesson) ? editedLesson.youTubeKey : undefined
  );
  const [textLessonContent, setTextLessonContent] = useState<string | undefined>(isLesson(editedLesson) ? editedLesson.text : undefined);
  const [calculatorLessonContent, setCalculatorLessonContent] = useState<CalculatorLesson>(
    isLesson(editedLesson) && editedLesson.kind == 'Calculator'
      ? {
          calculatorKind: editedLesson.calculatorKind as CalculatorKind,
          calculatorOptions: editedLesson.calculatorOptions as string,
        }
      : defaultCalculatorValue
  );
  const [chapterTestContent, setChapterTestContent] = useState<string | undefined>(
    !isLesson(editedLesson) ? editedLesson?.enterpoint : undefined
  );
  const [chapterTestId, setChapterTestId] = useState<string | undefined>(
    !isLesson(editedLesson) ? (editedLesson && 'id' in editedLesson ? editedLesson?.id : '') : undefined
  );

  const lessonOptions: Record<LessonOption, string> = {
    Picture: 'Image',
    Video: 'Video',
    Text: 'Text',
    Calculator: 'Calculator',
    ChapterTest: 'Chapter Test',
  };

  const handleAddLesson = () => {
    if (lessonType === 'ChapterTest') {
      createChapterTest();
    } else {
      createLesson(lessonType);
    }
  };

  const createChapterTest = () => {
    const newChapterTest: BaseAdminChapterTest | ExistingAdminChapterTest = chapterTestId
      ? { title: lessonTitle, enterpoint: chapterTestContent as string, id: chapterTestId }
      : { title: lessonTitle, enterpoint: chapterTestContent as string };
    resetLocalState();
    onClose(newChapterTest);
  };

  const shouldDisableAddBtn =
    !lessonTitle &&
    (!pictureLessonImages ||
      !pictureLessonDescription ||
      !videoLessonContent ||
      !textLessonContent ||
      !calculatorLessonContent ||
      !chapterTestContent);

  const createLesson = (type: AdminLessonType) => {
    const newLessonBase: Pick<BaseAdminLesson, 'title' | 'kind'> | Pick<AdminEditedLesson, 'title' | 'kind' | 'id'> =
      editedLesson && 'id' in editedLesson ? { title: lessonTitle, kind: type, id: editedLesson.id } : { title: lessonTitle, kind: type };
    let newLesson: BaseAdminLesson;

    if (type === 'Calculator') {
      newLesson = {
        ...newLessonBase,
        calculatorKind: calculatorLessonContent?.calculatorKind,
        calculatorOptions: calculatorLessonContent?.calculatorOptions,
      };
    } else if (type === 'Picture') {
      newLesson = {
        ...newLessonBase,
        medias: pictureLessonImages,
        text: pictureLessonDescription,
      };
    } else if (type === 'Video') {
      newLesson = {
        ...newLessonBase,
        youTubeKey: videoLessonContent,
      };
    } else {
      newLesson = {
        ...newLessonBase,
        text: textLessonContent,
      };
    }
    resetLocalState();
    onClose(newLesson);
  };

  const handleClose = () => {
    resetLocalState();
    onClose(undefined);
  };

  const resetLocalState = () => {
    setCalculatorLessonContent(defaultCalculatorValue);
    setPictureLessonImages([]);
    setPictureLessonDescription('');
    setVideoLessonContent('');
    setTextLessonContent('');
    setChapterTestContent('');
    setLessonTitle('');
    setLessonType('Picture');
  };

  return (
    <Modal
      className='edit-lesson-modal'
      cancelBtn={{
        label: 'Cancel',
        onClick: handleClose,
      }}
      confirmBtn={{
        label: editedLesson ? 'Edit' : 'Add',
        onClick: handleAddLesson,
        disabled: shouldDisableAddBtn,
      }}
      onClose={handleClose}
      title={editedLesson ? 'Edit Lesson' : 'Add Lesson'}
      titleIcon={editedLesson ? <Pencil /> : <Plus />}
      open={open}
    >
      <div className='edit-lesson-content'>
        <Input
          label='Title'
          value={lessonTitle}
          required
          onChange={(e) => setLessonTitle(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />

        <Select
          label='Choose an option'
          value={lessonType}
          required
          selectOptions={Object.entries(lessonOptions).map(([key, value]) => {
            return {
              label: value,
              value: key,
            };
          })}
          onChange={(e) => setLessonType(e.target.value as LessonOption)}
        />

        {lessonType === 'Video' && (
          <VideoLessonField lessonContent={videoLessonContent as string} handleLessonContent={setVideoLessonContent} />
        )}
        {lessonType === 'Picture' && (
          <PictureLessonField
            images={pictureLessonImages as AdminMedia[]}
            description={pictureLessonDescription as string}
            handleImages={setPictureLessonImages}
            handleDescription={setPictureLessonDescription}
          />
        )}
        {lessonType === 'Text' && (
          <TextLessonField lessonContent={textLessonContent as string} handleLessonContent={setTextLessonContent} />
        )}
        {lessonType === 'Calculator' && (
          <CalculatorLessonField lessonContent={calculatorLessonContent} handleLessonContent={setCalculatorLessonContent} />
        )}
        {lessonType === 'ChapterTest' && (
          <ChapterTestField
            lessonContent={chapterTestContent as string}
            id={chapterTestId as string}
            handleLessonContent={setChapterTestContent}
            handleLessonId={setChapterTestId}
          />
        )}
      </div>
    </Modal>
  );
};
